/**
 * Open / closed / unknown — the three states a marker and a sheet may show.
 *
 * "Closed" is a claim. Hours we cannot vouch for (`openState.known === false`,
 * including the stale-hours case) must not produce it, or we send someone
 * across town to a door we guessed was locked. The spec only paints
 * `[data-state="closed"]`; unknown falls through to the sage tile so the pin
 * still reads as a place, not as a shut shop.
 */

export const markerState = (spot) => {
  const state = spot?.openState;
  if (state?.known && state.open === false) return "closed";
  if (state?.known && state.open) return "open";
  return "unknown";
};

export const markerAriaLabel = (spot, primary) => {
  const name = primary || "Safe Spot";
  const state = markerState(spot);
  if (state === "open") return `Safe spot: ${name}, open now`;
  if (state === "closed") return `Safe spot: ${name}, closed`;
  // Unconfirmed hours still quote the declared line when we have one — that is
  // what the first map layer announced, and a screen reader should not go
  // silent just because we refuse to say open or closed.
  if (spot?.hours) return `Safe spot: ${name}, open ${spot.hours}`;
  return `Safe spot: ${name}`;
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * "opens 10:00" / "opens tomorrow 10:00" / "opens Sunday 10:00".
 *
 * The server sends opensDay (0=Sunday, Date#getDay numbering) beside opensAt,
 * but the copy used to print only the time, so a shop shut until Sunday read
 * as opening tomorrow morning. "Today" is the VENUE's day, in its time zone.
 * (Code review CARDS-TRADES-8 / LOCALE-6)
 */
export const opensPhrase = (state, timezone, now = new Date()) => {
  if (!state?.opensAt) return null;
  if (!Number.isInteger(state.opensDay)) return `opens ${state.opensAt}`;
  let today;
  let hhmm = "";
  try {
    const tz = timezone || "Asia/Jerusalem";
    today = WEEKDAYS.indexOf(new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: tz }).format(now));
    hhmm = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: tz }).format(now);
  } catch {
    today = now.getDay();
  }
  if (today < 0) today = now.getDay();
  const ahead = (state.opensDay - today + 7) % 7;
  // Same weekday but the opening time has gone by: that is NEXT week's.
  if (ahead === 0 && hhmm && hhmm >= state.opensAt) return `opens ${WEEKDAYS[state.opensDay]} ${state.opensAt}`;
  if (ahead === 0) return `opens ${state.opensAt}`;
  if (ahead === 1) return `opens tomorrow ${state.opensAt}`;
  return `opens ${WEEKDAYS[state.opensDay]} ${state.opensAt}`;
};

/** Copy for the sheet hours row. Never invents Closed from unknown hours. */
export const hoursCopy = (spot) => {
  const state = spot?.openState;
  if (!state?.known) {
    return { headline: "Hours not confirmed", tone: "unknown", sub: spot?.hours || "" };
  }
  if (state.open) {
    return {
      headline: state.closesAt ? `Open now · closes ${state.closesAt}` : "Open now",
      tone: "open",
      sub: spot?.hours || "",
    };
  }
  return {
    headline: state.opensAt ? `Closed · ${opensPhrase(state, spot?.timezone)}` : "Closed",
    tone: "closed",
    sub: spot?.hours || "",
  };
};
